import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  getToken,
  setToken,
  getUserInfo,
  setUserInfo,
  setInfoMap,
  getInfoMap,
  isSuperAdmin,
  hasPermission,
  clearAuth,
  clearAll,
  setSystemConfig,
  getSystemConfig
} from '@/utils/auth'
import { login as loginApi, logout as logoutApi } from '@/api/auth'
import { getSystemConfigApi } from '@/api/common'
import type { UserInfo, InfoMap } from '@/utils/auth'

/**
 * 用户与登录态 —— 对应旧 login.js 的登录流程 + common.js 的用户信息读取。
 *
 * 与旧逻辑保持一致的关键点：
 *   1) 密码提交前做 MD5（旧 login.js: $.md5($("#password").val())）
 *   2) 成功后写入 token / userInfo / infoMap 到 sessionStorage
 *   3) 非超管且 pcMaintenancSwitch != 'Y' 时，跳系统维护页（旧 login.js 分支）
 */
export const useUserStore = defineStore('user', () => {
  const token = ref(getToken())
  const userInfo = ref<UserInfo | null>(getUserInfo())
  const infoMap = ref<InfoMap | null>(getInfoMap())
  const systemConfig = ref<Record<string, string>>(getSystemConfig())

  const isLogged = computed(() => !!token.value)
  const isAdmin = computed(() => infoMap.value?.userRoleValue === '99')
  const userName = computed(() => userInfo.value?.trueName || userInfo.value?.userName || '')

  /** 加载系统配置（域名、版权、logo、公众号二维码） */
  async function loadSystemConfig(force = false) {
    if (!force && systemConfig.value.domainName) return systemConfig.value
    try {
      const cfg: any = await getSystemConfigApi()
      if (cfg) {
        setSystemConfig(cfg)
        systemConfig.value = getSystemConfig()
        if (cfg.pcMaintenancSwitch != null) {
          sessionStorage.setItem('pcMaintenancSwitch', String(cfg.pcMaintenancSwitch))
        }
      }
    } catch {
      /* 配置加载失败不阻断登录 */
    }
    return systemConfig.value
  }

  /**
   * 登录
   * @returns 需要跳转的目标路由
   */
  async function login(params: { userName: string; password: string; verifyCode?: string }) {
    const res: any = await loginApi({
      userName: params.userName,
      // 与旧工程一致：前端先 MD5
      password: md5(params.password),
      verifyCode: params.verifyCode
    })

    if (!res) throw new Error('登录失败')

    if (res.success === 'true') {
      setToken(res.accessToken)
      token.value = res.accessToken
      setUserInfo(res.user)
      userInfo.value = res.user
      if (res.infoMap) {
        setInfoMap(res.infoMap)
        infoMap.value = res.infoMap
      }

      const cfg = await loadSystemConfig(true)
      const maintenanceSwitch =
        sessionStorage.getItem('pcMaintenancSwitch') || (cfg as any)?.pcMaintenancSwitch

      if (isSuperAdmin() || maintenanceSwitch === 'Y') {
        return '/index'
      }
      return '/systemMaintenance'
    }

    // 旧逻辑：type == 'pw' -> 密码错误；否则用户名错误
    const err: any = new Error(res.type === 'pw' ? '密码错误' : '用户名或密码错误')
    err.type = res.type
    throw err
  }

  async function logout() {
    try {
      await logoutApi()
    } catch {
      /* 忽略退出接口异常，本地照样清理 */
    }
    clearAll()
    token.value = ''
    userInfo.value = null
    infoMap.value = null
  }

  /** 是否拥有按钮权限 */
  function checkPermission(code?: string | string[]) {
    return hasPermission(code)
  }

  return {
    token,
    userInfo,
    infoMap,
    systemConfig,
    isLogged,
    isAdmin,
    userName,
    loadSystemConfig,
    login,
    logout,
    checkPermission,
    clearAuth
  }
})

/**
 * MD5 —— 旧工程依赖 jquery.md5。
 * 这里内置一份最小实现，避免为一个函数引入整个 jquery 插件。
 * 计算结果与标准 MD5 一致。
 */
function md5(input: string): string {
  function safeAdd(x: number, y: number) {
    const lsw = (x & 0xffff) + (y & 0xffff)
    const msw = (x >> 16) + (y >> 16) + (lsw >> 16)
    return (msw << 16) | (lsw & 0xffff)
  }
  function bitRotateLeft(num: number, cnt: number) {
    return (num << cnt) | (num >>> (32 - cnt))
  }
  function md5cmn(q: number, a: number, b: number, x: number, s: number, t: number) {
    return safeAdd(bitRotateLeft(safeAdd(safeAdd(a, q), safeAdd(x, t)), s), b)
  }
  function md5ff(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
    return md5cmn((b & c) | (~b & d), a, b, x, s, t)
  }
  function md5gg(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
    return md5cmn((b & d) | (c & ~d), a, b, x, s, t)
  }
  function md5hh(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
    return md5cmn(b ^ c ^ d, a, b, x, s, t)
  }
  function md5ii(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
    return md5cmn(c ^ (b | ~d), a, b, x, s, t)
  }
  function binlMD5(x: number[], len: number) {
    x[len >> 5] |= 0x80 << len % 32
    x[(((len + 64) >>> 9) << 4) + 14] = len
    let a = 1732584193
    let b = -271733879
    let c = -1732584194
    let d = 271733878
    for (let i = 0; i < x.length; i += 16) {
      const olda = a
      const oldb = b
      const oldc = c
      const oldd = d
      a = md5ff(a, b, c, d, x[i], 7, -680876936)
      d = md5ff(d, a, b, c, x[i + 1], 12, -389564586)
      c = md5ff(c, d, a, b, x[i + 2], 17, 606105819)
      b = md5ff(b, c, d, a, x[i + 3], 22, -1044525330)
      a = md5ff(a, b, c, d, x[i + 4], 7, -176418897)
      d = md5ff(d, a, b, c, x[i + 5], 12, 1200080426)
      c = md5ff(c, d, a, b, x[i + 6], 17, -1473231341)
      b = md5ff(b, c, d, a, x[i + 7], 22, -45705983)
      a = md5ff(a, b, c, d, x[i + 8], 7, 1770035416)
      d = md5ff(d, a, b, c, x[i + 9], 12, -1958414417)
      c = md5ff(c, d, a, b, x[i + 10], 17, -42063)
      b = md5ff(b, c, d, a, x[i + 11], 22, -1990404162)
      a = md5ff(a, b, c, d, x[i + 12], 7, 1804603682)
      d = md5ff(d, a, b, c, x[i + 13], 12, -40341101)
      c = md5ff(c, d, a, b, x[i + 14], 17, -1502002290)
      b = md5ff(b, c, d, a, x[i + 15], 22, 1236535329)
      a = md5gg(a, b, c, d, x[i + 1], 5, -165796510)
      d = md5gg(d, a, b, c, x[i + 6], 9, -1069501632)
      c = md5gg(c, d, a, b, x[i + 11], 14, 643717713)
      b = md5gg(b, c, d, a, x[i], 20, -373897302)
      a = md5gg(a, b, c, d, x[i + 5], 5, -701558691)
      d = md5gg(d, a, b, c, x[i + 10], 9, 38016083)
      c = md5gg(c, d, a, b, x[i + 15], 14, -660478335)
      b = md5gg(b, c, d, a, x[i + 4], 20, -405537848)
      a = md5gg(a, b, c, d, x[i + 9], 5, 568446438)
      d = md5gg(d, a, b, c, x[i + 14], 9, -1019803690)
      c = md5gg(c, d, a, b, x[i + 3], 14, -187363961)
      b = md5gg(b, c, d, a, x[i + 8], 20, 1163531501)
      a = md5gg(a, b, c, d, x[i + 13], 5, -1444681467)
      d = md5gg(d, a, b, c, x[i + 2], 9, -51403784)
      c = md5gg(c, d, a, b, x[i + 7], 14, 1735328473)
      b = md5gg(b, c, d, a, x[i + 12], 20, -1926607734)
      a = md5hh(a, b, c, d, x[i + 5], 4, -378558)
      d = md5hh(d, a, b, c, x[i + 8], 11, -2022574463)
      c = md5hh(c, d, a, b, x[i + 11], 16, 1839030562)
      b = md5hh(b, c, d, a, x[i + 14], 23, -35309556)
      a = md5hh(a, b, c, d, x[i + 1], 4, -1530992060)
      d = md5hh(d, a, b, c, x[i + 4], 11, 1272893353)
      c = md5hh(c, d, a, b, x[i + 7], 16, -155497632)
      b = md5hh(b, c, d, a, x[i + 10], 23, -1094730640)
      a = md5hh(a, b, c, d, x[i + 13], 4, 681279174)
      d = md5hh(d, a, b, c, x[i], 11, -358537222)
      c = md5hh(c, d, a, b, x[i + 3], 16, -722521979)
      b = md5hh(b, c, d, a, x[i + 6], 23, 76029189)
      a = md5hh(a, b, c, d, x[i + 9], 4, -640364487)
      d = md5hh(d, a, b, c, x[i + 12], 11, -421815835)
      c = md5hh(c, d, a, b, x[i + 15], 16, 530742520)
      b = md5hh(b, c, d, a, x[i + 2], 23, -995338651)
      a = md5ii(a, b, c, d, x[i], 6, -198630844)
      d = md5ii(d, a, b, c, x[i + 7], 10, 1126891415)
      c = md5ii(c, d, a, b, x[i + 14], 15, -1416354905)
      b = md5ii(b, c, d, a, x[i + 5], 21, -57434055)
      a = md5ii(a, b, c, d, x[i + 12], 6, 1700485571)
      d = md5ii(d, a, b, c, x[i + 3], 10, -1894986606)
      c = md5ii(c, d, a, b, x[i + 10], 15, -1051523)
      b = md5ii(b, c, d, a, x[i + 1], 21, -2054922799)
      a = md5ii(a, b, c, d, x[i + 8], 6, 1873313359)
      d = md5ii(d, a, b, c, x[i + 15], 10, -30611744)
      c = md5ii(c, d, a, b, x[i + 6], 15, -1560198380)
      b = md5ii(b, c, d, a, x[i + 13], 21, 1309151649)
      a = md5ii(a, b, c, d, x[i + 4], 6, -145523070)
      d = md5ii(d, a, b, c, x[i + 11], 10, -1120210379)
      c = md5ii(c, d, a, b, x[i + 2], 15, 718787259)
      b = md5ii(b, c, d, a, x[i + 9], 21, -343485551)
      a = safeAdd(a, olda)
      b = safeAdd(b, oldb)
      c = safeAdd(c, oldc)
      d = safeAdd(d, oldd)
    }
    return [a, b, c, d]
  }
  function binl2rstr(input: number[]) {
    let output = ''
    for (let i = 0; i < input.length * 32; i += 8) {
      output += String.fromCharCode((input[i >> 5] >>> i % 32) & 0xff)
    }
    return output
  }
  function rstr2binl(input: string) {
    const output: number[] = []
    output[(input.length >> 2) - 1] = undefined as any
    for (let i = 0; i < output.length; i += 1) output[i] = 0
    for (let i = 0; i < input.length * 8; i += 8) {
      output[i >> 5] |= (input.charCodeAt(i / 8) & 0xff) << i % 32
    }
    return output
  }
  function rstrMD5(s: string) {
    return binl2rstr(binlMD5(rstr2binl(s), s.length * 8))
  }
  function rstr2hex(input: string) {
    const hexTab = '0123456789abcdef'
    let output = ''
    for (let i = 0; i < input.length; i += 1) {
      const x = input.charCodeAt(i)
      output += hexTab.charAt((x >>> 4) & 0x0f) + hexTab.charAt(x & 0x0f)
    }
    return output
  }
  function str2rstrUTF8(input: string) {
    return unescape(encodeURIComponent(input))
  }
  return rstr2hex(rstrMD5(str2rstrUTF8(input)))
}

export { md5 }
